import { COMPANY } from "@/lib/data";
import { Target, Compass, ShieldCheck } from "lucide-react";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-bone text-ink"
    >
      <div className="absolute inset-0 blueprint-grid opacity-60 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16">
          <div className="md:col-span-4">
            <span className="tech-rule">01 — About</span>
            <h2
              data-testid="about-heading"
              className="mt-6 font-heading font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tighter"
            >
              A civil firm <span className="text-rust italic font-medium">built on</span> roads, kerbs & concrete.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-lg leading-relaxed text-graphite">
              {COMPANY.name} is a Bengaluru-based civil construction and external
              development firm specializing in infrastructure for residential,
              commercial and industrial projects. Founded in {COMPANY.established} by
              proprietor <span className="text-ink font-medium">{COMPANY.proprietor}</span>,
              we have executed sustained external-development mandates for some of
              India's most respected developers and public agencies.
            </p>
            <p className="mt-6 text-base leading-relaxed text-graphite">
              Our work spans bituminous and concrete roadways, paver and kerb
              installations, granite supply, drainage networks, boundary works and
              rainwater harvesting — delivered with strict QA/QC and full IS-code
              compliance.
            </p>
          </div>
        </div>

        {/* Vision / Mission / Values grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 border border-ink">
          <Pillar
            testid="pillar-vision"
            icon={<Compass className="w-5 h-5" strokeWidth={2} />}
            tag="Vision"
            title="Trusted, recognized, sustainable."
            body="To be a leading civil engineering company recognized for excellence in quality, innovation and sustainable development."
          />
          <Pillar
            testid="pillar-mission"
            icon={<Target className="w-5 h-5" strokeWidth={2} />}
            tag="Mission"
            title="Reliable delivery, transparent operations."
            body="Deliver high-quality civil works with safety and efficiency; ensure client satisfaction through professionalism and transparent operations."
            highlight
          />
          <Pillar
            testid="pillar-values"
            icon={<ShieldCheck className="w-5 h-5" strokeWidth={2} />}
            tag="Values"
            title="Integrity at every kerb-line."
            body="Quality control to IS-code standards. Zero-accident culture. Environmentally responsible practice on every site."
          />
        </div>
      </div>
    </section>
  );
}

function Pillar({ icon, tag, title, body, testid, highlight }) {
  return (
    <div
      data-testid={testid}
      className={`p-8 md:p-10 border-ink md:[&:not(:last-child)]:border-r [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 ${
        highlight ? "bg-ink text-bone" : "bg-bone text-ink"
      }`}
    >
      <div className={`mb-5 inline-flex items-center justify-center w-10 h-10 border ${highlight ? "border-bone text-bone" : "border-ink text-ink"}`}>
        {icon}
      </div>
      <div className={`font-mono text-[11px] tracking-[0.22em] uppercase mb-3 ${highlight ? "text-rust" : "text-rust"}`}>
        {tag}
      </div>
      <h3 className="font-heading font-bold text-xl md:text-2xl tracking-tight leading-tight mb-3">
        {title}
      </h3>
      <p className={`text-sm leading-relaxed ${highlight ? "text-bone/75" : "text-graphite"}`}>
        {body}
      </p>
    </div>
  );
}
